import { motion } from 'framer-motion';
import { StarIcon } from '@heroicons/react/24/solid';
import { BAKERY_LOCAL } from '../utils/bakeryLocal';
import {
  DEFAULT_TESTIMONIALS,
  type HomepageTestimonialItem,
} from '../utils/homepageSectionDefaults';

/**
 * Customer testimonials section.
 * Copy and quotes come from Homepage Editor → Sections (with safe defaults).
 * Keep the Google / Facebook review buttons — those are not CMS content.
 */

type TestimonialsProps = {
  title?: string;
  description?: string;
  items?: HomepageTestimonialItem[];
};

const StarRating = ({ rating }: { rating: number }) => (
  <div className="flex gap-0.5">
    {[...Array(5)].map((_, i) => (
      <StarIcon
        key={i}
        className={`w-5 h-5 ${i < rating ? 'text-yellow-400' : 'text-gray-300'}`}
      />
    ))}
  </div>
);

export default function Testimonials({
  title = DEFAULT_TESTIMONIALS.testimonials_title,
  description = DEFAULT_TESTIMONIALS.testimonials_description,
  items = DEFAULT_TESTIMONIALS.testimonials_items,
}: TestimonialsProps) {
  const list = items.length > 0 ? items : DEFAULT_TESTIMONIALS.testimonials_items;

  return (
    <section className="section-padding bg-white overflow-hidden">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl font-display font-bold text-gray-900 mb-4">{title}</h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">{description}</p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {list.map((testimonial, index) => (
            <motion.div
              key={`${testimonial.name}-${index}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-bakery-cream rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow relative"
            >
              <div className="absolute -top-2 -left-2 text-6xl text-primary-200 font-serif leading-none">
                &quot;
              </div>
              <div className="mb-3">
                <StarRating rating={testimonial.rating} />
              </div>
              <p className="text-gray-700 mb-4 relative z-10 italic">&quot;{testimonial.text}&quot;</p>
              <div className="border-t border-primary-100 pt-4">
                <p className="font-semibold text-gray-900">{testimonial.name}</p>
                {testimonial.occasion ? (
                  <p className="text-sm text-primary-600">{testimonial.occasion}</p>
                ) : null}
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
          className="text-center mt-10"
        >
          <p className="text-gray-600 mb-4">Love our treats? We&apos;d love to hear from you!</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href={BAKERY_LOCAL.googleReviewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-full shadow-sm hover:shadow-md hover:border-gray-300 transition-all text-gray-700 font-medium"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Leave a Review on Google
            </a>
            <a
              href="https://www.facebook.com/profile.php/?id=100086623726840&sk=reviews"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-full shadow-sm hover:shadow-md hover:border-gray-300 transition-all text-gray-700 font-medium"
            >
              <svg className="w-5 h-5 text-[#1877F2]" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
              </svg>
              Leave a Review on Facebook
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
